import { useCallback, useEffect, useState } from 'react'
import {
  Button, Checkbox, Grid, Icon, Input, Loader, Message, Popup, Segment, Select, Table
} from 'semantic-ui-react'
import { toast } from 'react-toastify'
import { HeaderPage } from '../../../components/Admin'
import {
  FacturaAnalisisModal, FilePreviewLink, Reports, getPermissions
} from '../../../components/Common'
import { getFacturas, getReporteFacturas, updateFactura } from '../../../api/Facturas/facturas'
import { addHash } from '../../../api/Common/report'
import { useAuth, useNotification } from '../../../hooks'
import './FacturasList.scss'

const TIPOS_CARGA = {
  lizen: [
    { key: 'lizen_facturacion', text: 'Facturación', value: 'lizen_facturacion' },
    { key: 'lizen_cliente_factura', text: 'Directo a cliente', value: 'lizen_cliente_factura' }
  ],
  orion: [
    { key: 'orion_facturacion_archivo', text: 'Facturación', value: 'orion_facturacion_archivo' },
    { key: 'orion_clinica_documento', text: 'Clínica', value: 'orion_clinica_documento' }
  ]
}

const ESTADOS = [
  { key: 'procesado', text: 'Procesado', value: 'procesado' },
  { key: 'error', text: 'Error', value: 'error' },
  { key: 'manual', text: 'Cargado a mano', value: 'manual' }
]

const FILTROS_VACIOS = {
  search: '',
  tipo_carga: '',
  estado_analisis: '',
  vencimiento_desde: '',
  vencimiento_hasta: '',
  solo_por_vencer: false
}

// El checkbox viaja como 'true' o vacio; el resto de los filtros tal cual
const filtrosAQuery = (filtros) => ({
  ...filtros,
  solo_por_vencer: filtros.solo_por_vencer ? 'true' : ''
})

const formatearFecha = (fecha) => {
  if (!fecha) return '-'
  const [anio, mes, dia] = fecha.split('-')
  return `${dia}/${mes}/${anio}`
}

const formatearImporte = (importe, moneda) => {
  if (importe === null || importe === undefined || importe === '') return '-'
  const numero = Number(importe)
  if (Number.isNaN(numero)) return importe
  return `${moneda || 'ARS'} ${numero.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

const textoVencimiento = (factura) => {
  const { dias_para_vencer: dias, alerta } = factura

  if (dias === null || dias === undefined) return ''
  if (alerta === 'vencida') {
    const atraso = Math.abs(dias)
    return atraso === 0 ? 'Vence hoy' : `Vencida hace ${atraso} día${atraso === 1 ? '' : 's'}`
  }
  if (alerta === 'por_vencer') {
    return dias === 0 ? 'Vence hoy' : `Vence en ${dias} día${dias === 1 ? '' : 's'}`
  }
  return ''
}

/**
 * Listado de facturas de un dominio (Lizen u Orion), ordenado por vencimiento.
 * Las vencidas y las próximas a vencer se marcan en rojo.
 *
 * Todas las facturas se listan, pero el archivo y la edición solo están disponibles
 * para quien la cargó o para quien tiene el permiso `view_todas_facturas`: el backend
 * lo indica con `puede_ver` y no manda `url_archivo` en las ajenas.
 */
export function FacturasList({ origen }) {
  const [facturas, setFacturas] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtros, setFiltros] = useState(FILTROS_VACIOS)
  const [enEdicion, setEnEdicion] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const { auth } = useAuth()
  const { startInterval } = useNotification()

  const esLizen = origen === 'lizen'

  const fetchFacturas = useCallback(async (filtrosActivos) => {
    setLoading(true)
    try {
      const respuesta = await getFacturas(origen, filtrosAQuery(filtrosActivos))
      setFacturas(respuesta.results || [])
    } catch (error) {
      toast.error('No se pudieron cargar las facturas.')
      setFacturas([])
    } finally {
      setLoading(false)
    }
  }, [origen])

  useEffect(() => {
    setFiltros(FILTROS_VACIOS)
    fetchFacturas(FILTROS_VACIOS)
  }, [fetchFacturas])

  const aplicarFiltro = (campo, valor) => {
    const nuevos = { ...filtros, [campo]: valor }
    setFiltros(nuevos)
    fetchFacturas(nuevos)
  }

  const limpiarFiltros = () => {
    setFiltros(FILTROS_VACIOS)
    fetchFacturas(FILTROS_VACIOS)
  }

  // Mismo flujo que los demas reportes: se guarda el hash y el NotificationContext
  // avisa con la campanita cuando el Excel esta listo para descargar.
  const onReports = async () => {
    try {
      const respuesta = await getReporteFacturas(origen, filtrosAQuery(filtros))
      addHash(respuesta.hash)
      startInterval()
      toast.info('El reporte se está generando. Te avisamos en las notificaciones cuando esté listo.')
    } catch (error) {
      toast.error('No se pudo generar el reporte.')
    }
  }

  const onGuardarEdicion = async (valores) => {
    setGuardando(true)
    try {
      await updateFactura(enEdicion.id, valores)
      toast.success('Factura actualizada.')
      setEnEdicion(null)
      fetchFacturas(filtros)
    } catch (error) {
      toast.error('No se pudo actualizar la factura.')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div>
      <HeaderPage title={`Facturas ${esLizen ? 'Lizen' : 'Orion'}`} />

      <Grid as={Segment} className="segment_search">
        <Grid.Row>
          <Grid.Column computer={4} mobile={16} tablet={8}>
            <Input
              fluid
              icon={<Icon name="search" className="color_search" inverted circular />}
              className="input_search"
              inverted
              placeholder="Buscar..."
              value={filtros.search}
              onChange={(e) => aplicarFiltro('search', e.target.value)}
            />
          </Grid.Column>
          <Grid.Column computer={3} mobile={16} tablet={8}>
            <Select
              fluid
              clearable
              placeholder="Tipo"
              options={TIPOS_CARGA[origen] || []}
              value={filtros.tipo_carga}
              onChange={(_, data) => aplicarFiltro('tipo_carga', data.value || '')}
            />
          </Grid.Column>
          <Grid.Column computer={3} mobile={16} tablet={8}>
            <Select
              fluid
              clearable
              placeholder="Estado"
              options={ESTADOS}
              value={filtros.estado_analisis}
              onChange={(_, data) => aplicarFiltro('estado_analisis', data.value || '')}
            />
          </Grid.Column>
          <Grid.Column computer={2} mobile={16} tablet={8}>
            <Input
              fluid
              type="date"
              value={filtros.vencimiento_desde}
              onChange={(e) => aplicarFiltro('vencimiento_desde', e.target.value)}
            />
          </Grid.Column>
          <Grid.Column computer={2} mobile={16} tablet={8}>
            <Input
              fluid
              type="date"
              value={filtros.vencimiento_hasta}
              onChange={(e) => aplicarFiltro('vencimiento_hasta', e.target.value)}
            />
          </Grid.Column>
          <Grid.Column computer={2} mobile={16} tablet={8}>
            <Button basic circular fluid onClick={limpiarFiltros}>
              Limpiar
            </Button>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row verticalAlign="middle">
          <Grid.Column computer={12} mobile={16} tablet={10}>
            <Checkbox
              label="Ver solo las vencidas y próximas a vencer"
              checked={filtros.solo_por_vencer}
              onChange={(_, data) => aplicarFiltro('solo_por_vencer', data.checked)}
            />
          </Grid.Column>
          <Grid.Column computer={4} mobile={16} tablet={6} textAlign="right">
            {getPermissions('generate_report', auth) && (
              <Reports confirmReports={onReports} />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {loading ? (
        <Loader active inline="centered">
          Cargando...
        </Loader>
      ) : facturas.length === 0 ? (
        <Message>No hay facturas registradas.</Message>
      ) : (
        <Table celled selectable className="facturas-table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Vencimiento</Table.HeaderCell>
              <Table.HeaderCell>Aseguradora</Table.HeaderCell>
              <Table.HeaderCell>{esLizen ? 'Cliente' : 'Beneficiario'}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Monto</Table.HeaderCell>
              <Table.HeaderCell>N° factura</Table.HeaderCell>
              <Table.HeaderCell>Tipo</Table.HeaderCell>
              <Table.HeaderCell>Estado</Table.HeaderCell>
              <Table.HeaderCell>Cargada por</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Acciones</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {facturas.map((factura) => (
              <Table.Row key={factura.id} className={`factura-${factura.alerta}`}>
                <Table.Cell>
                  <strong>{formatearFecha(factura.fecha_vencimiento)}</strong>
                  {textoVencimiento(factura) && (
                    <div className="facturas-table__aviso">{textoVencimiento(factura)}</div>
                  )}
                </Table.Cell>
                <Table.Cell>{factura.aseguradora_nombre || '-'}</Table.Cell>
                <Table.Cell>{factura.cliente_nombre || '-'}</Table.Cell>
                <Table.Cell textAlign="right">
                  {formatearImporte(factura.importe_total, factura.moneda)}
                </Table.Cell>
                <Table.Cell>
                  {factura.numero_factura || '-'}
                  {factura.tipo_comprobante ? ` (${factura.tipo_comprobante})` : ''}
                </Table.Cell>
                <Table.Cell>{factura.tipo_carga_display}</Table.Cell>
                <Table.Cell>
                  {factura.estado_analisis_display}
                  {factura.editado_manual && (
                    <Popup
                      content="Corregida a mano"
                      trigger={<Icon name="pencil" size="small" style={{ marginLeft: '0.4em' }} />}
                    />
                  )}
                </Table.Cell>
                <Table.Cell>{factura.created_by_nombre || '-'}</Table.Cell>
                <Table.Cell textAlign="center">
                  {factura.puede_ver ? (
                    <>
                      <FilePreviewLink filePath={factura.url_archivo}>
                        <Icon name="file outline" />
                      </FilePreviewLink>
                      <Popup
                        content="Editar datos"
                        trigger={
                          <Button
                            icon
                            basic
                            circular
                            size="mini"
                            onClick={() => setEnEdicion(factura)}
                          >
                            <Icon name="edit" />
                          </Button>
                        }
                      />
                    </>
                  ) : (
                    <Popup
                      content="Solo el usuario que la cargó puede abrirla"
                      trigger={<Icon name="lock" color="grey" />}
                    />
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {enEdicion && (
        <FacturaAnalisisModal
          open
          modoEdicion
          guardando={guardando}
          urlArchivo={enEdicion.url_archivo}
          datos={enEdicion}
          aseguradoraNombre={enEdicion.aseguradora_nombre}
          clienteNombre={enEdicion.cliente_nombre}
          onConfirm={onGuardarEdicion}
          onClose={() => setEnEdicion(null)}
        />
      )}
    </div>
  )
}
